import React from 'react';

/**
 * A code-split, prop-less component that can be loaded *before* it is first
 * rendered.
 *
 * Plain `React.lazy` suspends on first render even when the underlying chunk is
 * already in webpack's module cache, because React only learns the module resolved
 * by attaching a `.then` during that render. On a prerendered page that is fatal:
 * a Suspense boundary resolving mid-hydration makes React discard the server HTML
 * and rebuild the DOM, which is the flash this whole change set removes.
 *
 * So the module is captured here as well. Once `preload()` has resolved, rendering
 * is a direct synchronous call and no Suspense boundary is ever reached. The lazy
 * path remains as a fallback for the case where preload was skipped or failed.
 *
 * `factory` is invoked at most once per module: webpack memoises the import, and
 * repeated calls return the same settled promise.
 *
 * Prop-less by design — this exists for route components, which the router renders
 * without props. Anything needing props should use React.lazy with a Suspense
 * boundary, as AppShell and SignInModal do.
 */
export interface PreloadableComponent extends React.FC {
    preload(): Promise<unknown>;
}

export function lazyWithPreload(
    factory: () => Promise<{ default: React.ComponentType }>
): PreloadableComponent {
    let loaded: React.ComponentType | null = null;
    let inFlight: Promise<unknown> | null = null;

    const Lazy = React.lazy(factory);

    const Component: PreloadableComponent = Object.assign(
        () => React.createElement(loaded ?? Lazy),
        {
            preload: () => {
                if (!inFlight) {
                    inFlight = factory().then(mod => {
                        loaded = mod.default;
                        return mod;
                    });
                }
                return inFlight;
            },
        }
    );

    return Component;
}

/** Narrows an arbitrary component to one that exposes `preload()`. */
export function isPreloadable(component: unknown): component is PreloadableComponent {
    return typeof component === 'function' && typeof (component as PreloadableComponent).preload === 'function';
}
